// src/components/blog/BlogDetail.tsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GetLastBlogByTitle } from "../../services/blog/GetLastBlogByTitle";
import "../../styles/blog/BlogDetail.css";
import LoadingSpinner from "../utility/LoadingSpinner";
import { useTranslation } from "react-i18next";

interface BlogData {
  id: number;
  date: string;
  title: string;
  description: string;
  imageName: string;
  seo: string;
  status: number;
}

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const [blog, setBlog] = useState<BlogData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const URL_BACK_IMG = process.env.REACT_APP_API_IMGBLOG_URL;

  useEffect(() => {
    const fetchBlog = async () => {
      if (!slug) {
        setError(t("blog.slugNotFound"));
        setLoading(false);
        return;
      }

      try {
        const formattedTitle = slug.replace(/-/g, " ");
        const blogData = await GetLastBlogByTitle(formattedTitle);

        console.log(blogData,"blog url")

        if (!blogData) {
          setError(t("blog.blogNotFound"));
        } else {
          setBlog(blogData);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t("blog.unknownError")
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug, t]);

  const handleGoBack = () => {
    navigate("/blogs");
  };

  if (loading) {
    return (
      <div className="blog-detail">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-detail">
        <button onClick={handleGoBack} className="blog-detail-back-button">
          {t("blog.goBack")}
        </button>
        <div className="blog-detail-error">{t("blog.errorPrefix")} {error}</div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="blog-detail">
        <button onClick={handleGoBack} className="blog-detail-back-button">
          {t("blog.goBack")}
        </button>
        <div className="blog-detail-not-found">
          {t("blog.blogNotFound")}
        </div>
      </div>
    );
  }

  // Formatear fecha para SEO
  const publishedDate = new Date(blog.date).toISOString();
  const displayDate = new Date(blog.date).toLocaleDateString(i18n.language, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  console.log(`${URL_BACK_IMG}${blog.imageName}`,"imageNameeeeeeeeeee")

  return (
    <>
      {/* Metaetiquetas usando React 19 - React las moverá automáticamente al <head> */}
      <title>{`${t("blog.metaTitlePrefix")}${blog.title}`}</title>
      <meta name="description" content={blog.description} />
      <meta name="keywords" content={blog.seo} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={blog.title} />
      <meta property="og:description" content={blog.description} />
      <meta property="og:image" content={`${URL_BACK_IMG}${blog.imageName}`} />
      <meta property="og:url" content={window.location.href} />
      <meta property="og:site_name" content={t("blog.metaSiteName")} />
      <meta property="article:published_time" content={publishedDate} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={blog.title} />
      <meta name="twitter:description" content={blog.description} />
      <meta name="twitter:image" content={`${URL_BACK_IMG}${blog.imageName}`} />

      {/* Schema.org markup for Google - usando script tag */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": blog.title,
          "description": blog.description,
          "image": `${URL_BACK_IMG}${blog.imageName}`,
          "datePublished": publishedDate,
          "author": {
            "@type": "Organization",
            "name": t("blog.metaAuthor")
          },
          "publisher": {
            "@type": "Organization",
            "name": t("blog.metaAuthor"),
            "logo": {
              "@type": "ImageObject",
              "url": `${window.location.origin}/logo.png`
            }
          }
        })}
      </script>

      <div className="blog-detail">
        <button onClick={handleGoBack} className="blog-detail-back-button">
          {t("blog.goBack")}
        </button>

        <div className="blog-detail-image-container">
          <img
            src={`${URL_BACK_IMG}${blog.imageName}`}
            alt={blog.title}
            className="blog-detail-image"
            loading="lazy"
          />
        </div>
        <p className="blog-detail-date">{displayDate}</p>
        <h1 className="blog-detail-title">{blog.title}</h1>
        <p className="blog-detail-description">{blog.description}</p>
      </div>
    </>
  );
};

export default BlogDetail;